import { AnimatePresence, motion } from "framer-motion"
import CompanyCard from "./CompanyCard"
import { RefreshCw } from "react-feather"
import Button from "../UIComponents/Button"
import useCompanySwipeStack from "../../hooks/useCompanySwipeStack"
import { useCallback, useEffect, useState } from "react"
import { useProfileStore } from "../../state-management/profileStore"
import { useShallow } from "zustand/react/shallow"
import { useDataStore } from "../../state-management/dataStore"
import { useLanguageStore } from "../../state-management/languageStore"

function MatchCompany({ onBackgroundChange, onInitialChange, themeColor }) {
    const data = useDataStore((state) => state.data)
    const { favorites, setFavorites } = useProfileStore(
        useShallow((state) => ({
            favorites: state.favorites,
            setFavorites: state.setFavorites,
        })),
    )

    const [isTransitioning, setIsTransitioning] = useState(false)
    const t = useLanguageStore((state) => state.translate)

    const {
        currentCompany,
        handleSwipe,
        refreshStack,
        clearRejectedCompanies,
        isStackEmpty,
        canRefresh,
        allCompaniesLiked,
    } = useCompanySwipeStack(data, favorites)

    // Nollställning när det är slut på företag
    useEffect(() => {
        if (isStackEmpty) {
            onBackgroundChange(themeColor)
            onInitialChange(true)
        }
    }, [isStackEmpty, themeColor, onBackgroundChange, onInitialChange])

    const handleRefresh = () => {
        setIsTransitioning(false)
        onInitialChange(true)
        clearRejectedCompanies()
        refreshStack()
        onBackgroundChange(themeColor)
    }

    const handleOnDragChange = (backgroundColor) => {
        onInitialChange(false)
        onBackgroundChange(backgroundColor)
    }

    const handleLike = useCallback(() => {
        if (currentCompany && !isTransitioning) {
            setIsTransitioning(true)
            requestAnimationFrame(() => {
                setFavorites(currentCompany)
                handleSwipe(true)
            })
        }
    }, [currentCompany, isTransitioning, setFavorites, handleSwipe])

    const handleRejection = useCallback(() => {
        if (!isTransitioning) {
            setIsTransitioning(true)
            handleSwipe(false)
        }
    }, [isTransitioning, handleSwipe])

    // Returnera detta om alla företag är gillade (i favoriter)
    if (allCompaniesLiked) {
        return (
            <div className="w-full h-full grid place-items-center relative">
                <div className="flex flex-col m-4">
                    <p className="pb-3 text-center fg-color-2">
                        {t("match.allLikedTitle")}
                        <br />
                        {t("match.allLikedBody")}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <motion.div
            className="w-full h-full flex justify-center items-center sm:mb-0 mb-20 relative "
            data-disable-swipe
        >
            {isStackEmpty && canRefresh ? (
                <div className="flex flex-col m-4">
                    <p className="pb-3 text-center fg-color-1">
                        {t("match.emptyTitle")}
                        <br />
                        {t("match.emptyBody")}
                    </p>
                    <Button onClick={handleRefresh} classes={"p-4 mx-auto"}>
                        <RefreshCw size={32} />
                        <span className="sr-only">{t("match.reload")}</span>
                    </Button>
                </div>
            ) : (
                <AnimatePresence
                    mode="wait"
                    onExitComplete={() => {
                        setIsTransitioning(false)
                        onInitialChange(true)
                    }}
                    reducedMotion="user"
                >
                    {currentCompany && !isTransitioning && (
                        <CompanyCard
                            key={`company-${currentCompany.monternummer}`}
                            data={currentCompany}
                            setInitial={onInitialChange}
                            themeColor={themeColor}
                            onDragChange={handleOnDragChange}
                            handleLike={handleLike}
                            handleRejection={handleRejection}
                        />
                    )}
                </AnimatePresence>
            )}
        </motion.div>
    )
}

export default MatchCompany
